<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useApi } from '@/composables/useApi'
import { useUser } from '@/composables/useUser'
import { useCurrentUser } from '@/composables/useCurrentUser'

const props = defineProps({
  isOpen: Boolean,
  itemData: { type: Object, default: null },
  ranks: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'save'])

// Form fields - không có role_id vì customer luôn cố định role 2
const initialForm = {
  id: null,
  name: '',
  email: '',
  phone: '',
  password: '',
  rank_id: '',
  sale_id: '',
  auto_buy_tracking_flg: 0,
  auto_pay_order_flg: 0,
}

const formData = reactive({ ...initialForm })

const isEdit = computed(() => !!props.itemData && !!props.itemData.id)

// Chỉ admin được gán Sale cho customer
const { currentUser } = useCurrentUser()
const isAdmin = computed(() =>
  currentUser.value?.roles?.some(r => {
    const name = (r.name || '').toLowerCase()
    return name === 'admin' || name === 'administrator' || name === 'owner'
  })
)

// Danh sách user có role sale (role_id:5) để chọn
const { getUsersApi } = useUser()
const sales = ref([])
const loadSales = async () => {
  if (sales.value.length) return
  const res = await getUsersApi({ search: 'role_id:5', limit: 1000, orderBy: 'name', sortedBy: 'asc' })
  if (res.success && res.data) {
    sales.value = res.data.data || []
  }
}

// Reset / populate form khi mở/đóng
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    if (isAdmin.value) loadSales()
    if (props.itemData) {
      Object.keys(initialForm).forEach(key => {
        formData[key] = props.itemData[key] !== undefined ? props.itemData[key] : initialForm[key]
      })
      // API trả về customerRank là object, không trả về rank_id trực tiếp
      formData.rank_id = props.itemData.customerRank?.id ?? props.itemData.rank_id ?? ''
      // API trả về trong additional_data
      formData.auto_buy_tracking_flg = props.itemData.additional_data?.auto_buy_tracking_flg ?? 0
      formData.auto_pay_order_flg = props.itemData.additional_data?.auto_pay_order_flg ?? 0
    } else {
      Object.keys(initialForm).forEach(key => {
        formData[key] = initialForm[key]
      })
    }
  } else {
    // Đóng modal → clear form
    Object.keys(initialForm).forEach(key => {
      formData[key] = initialForm[key]
    })
  }
}, { immediate: true })

const closeForm = () => {
  emit('close')
}

const handleSubmit = () => {
  const submitData = {}
  Object.keys(initialForm).forEach(key => {
    if (key !== 'id') submitData[key] = formData[key]
  })
  if (isEdit.value) submitData.id = formData.id
  // Không gửi password rỗng khi edit
  if (isEdit.value && !submitData.password) {
    delete submitData.password
  }
  // Chỉ admin được set sale_id (gán hoặc bỏ gán). Non-admin không gửi để khỏi đụng giá trị cũ.
  if (!isAdmin.value) {
    delete submitData.sale_id
  }
  emit('save', submitData)
}
</script>

<template>
  <div v-if="isOpen" class="form-sidebar-overlay" @click.self="closeForm">
    <div class="form-sidebar">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="fw-bold m-0">{{ isEdit ? 'Edit Customer' : 'Add Customer' }}</h4>
        <button class="btn-close" @click="closeForm"></button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label class="form-label fw-semibold">Name <span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="formData.name" placeholder="Enter name" required>
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Email <span class="text-danger">*</span></label>
          <input type="email" class="form-control" v-model="formData.email" placeholder="Enter email" required>
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Phone</label>
          <input type="text" class="form-control" v-model="formData.phone" placeholder="Enter phone">
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">
            {{ isEdit ? 'New Password' : 'Password' }}
            <span v-if="!isEdit" class="text-danger">*</span>
            <small v-else class="text-muted fw-normal">(để trống nếu không đổi)</small>
          </label>
          <input 
            type="password" 
            class="form-control" 
            v-model="formData.password" 
            placeholder="Enter password"
            :required="!isEdit"
          >
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Rank</label>
          <select class="form-select" v-model="formData.rank_id">
            <option value="">No Rank</option>
            <option v-for="rank in ranks" :key="rank.id" :value="rank.id">
              {{ rank.name }}
            </option>
          </select>
        </div>

        <div class="mb-3" v-if="isAdmin">
          <label class="form-label fw-semibold">Sale phụ trách</label>
          <select class="form-select" v-model="formData.sale_id">
            <option value="">-- Chưa gán --</option>
            <option v-for="s in sales" :key="s.id" :value="s.id">
              {{ s.name }} <template v-if="s.email">({{ s.email }})</template>
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Auto Buy Tracking</label>
          <select class="form-select" v-model="formData.auto_buy_tracking_flg">
            <option :value="0">No</option>
            <option :value="1">Yes</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Auto Pay Order</label>
          <select class="form-select" v-model="formData.auto_pay_order_flg">
            <option :value="0">No</option>
            <option :value="1">Yes</option>
          </select>
        </div>

        <div class="d-flex gap-2 mt-4">
          <button type="button" class="btn btn-secondary w-100" @click="closeForm">Cancel</button>
          <button type="submit" class="btn btn-primary w-100">
            {{ isEdit ? 'Update' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.form-sidebar-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4);
  z-index: 9999 !important;
}
.form-sidebar {
  position: fixed;
  top: 0; right: 0;
  width: 420px; height: 100%;
  background: white;
  padding: 30px;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  animation: slideIn 0.3s ease-out;
  overflow-y: auto;
}
@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
</style>